import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import * as Highcharts from 'highcharts';
import {GridDataResult} from "@progress/kendo-angular-grid";
import {orderBy, SortDescriptor} from "@progress/kendo-data-query";
import {IChartTitleData, ILargeCardInteriorOptions} from "app/modules/shared/interfaces/card-data";
import {NoDataActionType} from "app/modules/shared/interfaces/no-data";
import {
    IKPIDistribution,
    IDistributionParameters,
    KpiDistributionAnalysisService, IDistributionMetrics
} from "app/modules/settings/services/kpi-distribution-analysis.service";
import {ReusableFunctions} from "app/modules/shared/classes/reusable-functions";
import {KPIService} from "app/modules/shared/services/kpi.service";
import {CurrentUserStateService} from "app/modules/shared/services/current-user-state.service";
import { ImpersonationService } from 'app/modules/shared/services/impersonation.service';
import {
    IDistributionDialogData,
    IKPIConfigData,
    KPIConfigService
} from "app/modules/settings/services/kpi-config.service";

@Component({
    selector: 'app-distribution-analyze-dialog',
    templateUrl: './distribution-analyze-dialog.component.html',
    styleUrls: ['./distribution-analyze-dialog.component.scss']
})
export class DistributionAnalyzeDialogComponent implements OnInit {
    gridData: GridDataResult;
    sortable = {
        mode: 'single',
        allowUnsort: true,
    };

    sort: SortDescriptor[] = [
        {
            field: 'clientKPIDescription',
            dir: 'asc',
        },
    ];

    loading: boolean = false;
    histogramCard: ILargeCardInteriorOptions;
    boxplotCard: ILargeCardInteriorOptions;
    distributionInfo: IKPIDistribution;
    metrics: IDistributionMetrics;
    action: NoDataActionType;
    distributionParameters: IDistributionParameters;
    isAuthorized: boolean;
    configValueSet: number[];
    isErrorFree: boolean;
    dataCleaned: boolean;
    saveMessage: string;
    saveMessageColor: string;
    buttonOptions: Array<{
        id: string;
        name: string;
        disabled: boolean;
        class: string;
    }>;
    data:  IKPIConfigData;
    
    constructor(
        @Inject(MAT_DIALOG_DATA) public incomingData: IDistributionDialogData,
        private _distributionService: KpiDistributionAnalysisService,
        private _KPIService: KPIService,
        private _reusableFunctions: ReusableFunctions,
        private _currentUserStateService: CurrentUserStateService,
        private _impersonationService: ImpersonationService,
        private _kpiConfigService: KPIConfigService,
    ) {
    }

    ngOnInit() {
        this.dataCleaned = false;
        this.isErrorFree = false;
        this.saveMessage = '';
        this.data = this.incomingData.dataToChild;
        this.isAuthorized = this._currentUserStateService.isLBASuperUser() ||            
            this._currentUserStateService.isPowerUser() || 
            this._currentUserStateService.isPlatformUser();
        
        if (this.isAuthorized) {
            this.data['status'] = 'Current';
            this.gridData = {
                data: orderBy([this.data], this.sort),
                total: 0,
            };
            this.configValueSet = [];
            this.buttonOptions = [
                {
                    id: 'reset',
                    name: 'Reset Charts',
                    disabled: true,
                    class: '',
                },
                {
                    id: 'clean',
                    name: 'Clean data',
                    disabled: true,
                    class: '',
                },
                {
                    id: 'apply',
                    name: 'Apply to grid',
                    disabled: true,
                    class: '',
                },
                {
                    id: 'save',
                    name: 'Save',
                    disabled: true,
                    class: '',
                }, 
            ];
            this.action = NoDataActionType.pageStart;
            this.initializeLargeCard();
            
            this.distributionInfo = {
                mean: 0,
                median: 0,
                min: 0,
                max: 0,
                std: 0,
                mode: 0,
                count: 0,
                outlierCount: 0,
                cleanedByMinAndMax: "0", 
            };        
            this.getDistributionValues();
        }
    }

    setDistributionParameters(getCleanedData: '0' | '1') {      
        this.metrics = {
            mean: '--',
            median: '--',
            min: '--',
            max: '--',
            std: '--',
            mode: '--',
            count: '--',
            outlierCount: '--',           
        };        
        
        this.distributionParameters = this._distributionService.setDistributionParameters(
            this.data, 
            this.distributionInfo.cleanedByMinAndMax, 
            getCleanedData, '1');
        // TODO: Determine is zero is a valid data point for some KPIs.
        
        if (this.data.min !== null) {
            this.distributionParameters.givenMin = this.data.min.toString();
        }

        if (this.data.max !== null) {
            this.distributionParameters.givenMax = this.data.max.toString();
        }
        
        if (this.distributionInfo.interQuartileMax != null && this.distributionInfo.interQuartileMin != null) {
            this.distributionParameters.interQuartileMax = this.distributionInfo.interQuartileMax.toString();
            this.distributionParameters.interQuartileMin = this.distributionInfo.interQuartileMin.toString();
        }
    }

    formatVal(value: number, precision = this.data.precision, format = this.data.kpiFormat) {
        return this._reusableFunctions.formatNumber(value, precision, format);
    }
    getDistributionValues(getCleanedData: '0' | '1' = '0') {
        this.setDistributionParameters(getCleanedData);
        let cards = [this.histogramCard, this.boxplotCard];
        
        cards.forEach((card, index) => {
            card.hasData = false;
            card.isDataReady = false;
            this.updateCardData(card, index);
        })
            
        this._distributionService.getKpiDistributions(this.distributionParameters)
            .subscribe((res) => {
                    if (res && res.count > 0) {
                        this.distributionInfo = res;
                        this.distributionInfo.outlierCount = res.outliersArray.length;

                        this.metrics = {
                            mean: this.formatVal(res.mean),
                            median: this.formatVal(res.median),
                            min: this.formatVal(res.min),
                            max: this.formatVal(res.max),
                            std: this.formatVal(res.std),
                            mode: this.formatVal(res.mode),
                            count: this.formatVal(res.count,0,'number'),
                            outlierCount: this.formatVal(res.outliersArray.length, 0,'number'),
                        };

                        this.setChartData();
                        this.setButtonActiveState();
                        
                        cards.forEach((card, index) => {
                            card.hasData = true;
                            card.isDataReady = true;
                            this.updateCardData(card, index);
                        })
                         
                    } else {
                        cards.forEach((card, index) => {
                            card.hasData = false;
                            card.isDataReady = true;
                            card.action = this.action;
                            this.updateCardData(card, index);
                        })
                    }
                },
                (error) => {
                    cards.forEach((card, index) => {
                        card.hasData = false;
                        card.isDataReady = true;
                        card.action = NoDataActionType.error;
                        this.updateCardData(card, index);
                    })
                });
    }

    updateCardData(
        card: ILargeCardInteriorOptions, 
        index: number) 
    {
        if (index === 0) {
            this.histogramCard = Object.assign({}, card);
        }
        else {
            this.boxplotCard = Object.assign({}, card);
        }
    }

    initializeLargeCard() {
        let card: ILargeCardInteriorOptions = {
            action: NoDataActionType.pageStart,
            componentTitle: 'Histogram',
            chartTitleData: {} as IChartTitleData,
            searchTerm: '',
            hasData: false,
            isDataReady: false,
            showTitle: true,
            accessToUrl: '',
            uniqueId: 'kpi-distribution-histogram',
            heightPercent: 99,
            minHeight: 269,
        };

        this.histogramCard = Object.assign({}, card);
        card.componentTitle = 'Normal Distribution';
        card.uniqueId = 'kpi-distribution-bellCurve';
        this.boxplotCard = Object.assign({}, card);
    }

    setChartData() {
        let histogramChartOptions = this.setHistogramChartOptions();
        let bellCurveChartOptiions = this.setBoxplotChartOptions();
        this.boxplotCard.chart = new Highcharts.Chart(this.boxplotCard.uniqueId, bellCurveChartOptiions);
        this.histogramCard.chart = new Highcharts.Chart(this.histogramCard.uniqueId, histogramChartOptions);
    }

    setHistogramChartOptions() {
        let data = this.distributionInfo.binInfoArray;
        let splineData = data.map((obj) => ({...obj}));
        let maLength = 15;
        let sumForMA = 0;
        splineData.map((obj, index) => {
            let begin = 0;
            let end = index + 1;
            if (index >= maLength) {
                begin = index + 1 - maLength;
                end = begin + maLength;
            }

            let dataFractionForMA = splineData.slice(begin, end);
            sumForMA = this._reusableFunctions.calculateSumByProperty(dataFractionForMA, 'probability');
            let ma = sumForMA / maLength;
            obj.y = +(ma * 100).toFixed(3)
        });
        let numberOfBins = this.distributionInfo.numberOfBins;
        let mean = this.distributionInfo.mean;
        let median = this.distributionInfo.median;

        return {
            chart: {
                defaultSeriesType: 'column',
                borderWidth: 0,
                borderColor: '#ccc',
                plotBorderWidth: 1,
                plotBorderColor: '#ccc',
                height: 250,
            },
            credits: {enabled: false},
            exporting: {enabled: false},
            title: {text: null},
            tooltip: {
                borderWidth: 1,
                backgroundColor: '#ffffff',
            },
            plotOptions: {
                column: {
                    shadow: false,
                    borderColor: 'rgba(204,204,204,.85)',
                    color: 'rgba(204,204,204,.85)',
                    showInLegend: false,
                },
                histogram: {
                    showInLegend: false,
                    color: 'rgba(204,204,204,.85)',
                },
            },
            xAxis: [{
                title: {text: 'No of Bins = ' + numberOfBins},
                labels: {
                    style: {
                        fontSize: '8px',
                        fontWeight: 'normal',
                        color: '#333'
                    },
                },
                lineWidth: 0,
                opposite: false,
                plotLines: [
                    {
                        value: mean,
                        color: 'orange',
                        width: 1,
                        label: {
                            text: 'mean [ ' + this.formatVal(mean) + ' ]',
                            rotation: 0,
                               style: {
                                color: 'orange',
                            },
                        }
                    },
                    {
                        value: median,
                        color: 'green',
                        
                        width: 1,
                        label: {
                            text: 'median [ ' + this.formatVal(median) + ' ]',
                            rotation: 0,
                            y: 25,
                            style: {
                                color: 'green',
                            },
                        }
                    },
                ],
            },
            ],
            yAxis: [
                {
                    title: {text: 'Occurence'},
                    gridLineColor: '#e9e9e9',
                    tickColor: '#ccc',
                    lineColor: '#ccc',
                    opposite: false,
                },
                {
                    title: {text: 'Probability %'},
                    gridLineColor: '#e9e9e9',
                    tickColor: '#ccc',
                    lineColor: '#ccc',
                    opposite: true,
                },
            ],
            series: [
                {
                    name: 'Occurence',
                    type: 'column',
                    xAxis: 0,
                    yAxis: 0,
                    data: data,
                },
                {
                    data: splineData,
                    name: 'Probability %',
                    visible: true,
                    type: "spline",
                    showInLegend: false,
                    yAxis: 1,
                    color: '#D66BA0',
                },
            ]
        } as Highcharts.Options
    }
    
    setBoxplotChartOptions() {      
        let category = this.distributionParameters.category;
        let outliers = [];
        let outlierArray = this.distributionInfo.outliersArray;
        let boxplotData = [
            this.distributionInfo.interQuartileMin,
            this.distributionInfo.quartile1,
            this.distributionInfo.quartile2,
            this.distributionInfo.quartile3,
            this.distributionInfo.interQuartileMax,
        ];
        if (outlierArray && outlierArray.length > 0) {
            //// ADD LOGIC TO MATCH CATEGORY NAMES; ex lowercase to camelcase
            outlierArray.forEach((item) => {
                outliers.push({
                    x: 0,
                    y: +item[category.toLowerCase()],
                });                
            });
        }
             return    {
                    chart: {
                        defaultSeriesType: 'column',
                        type: 'boxplot',
                        inverted: true,
                        borderWidth: 0,
                        borderColor: '#ccc',
                        plotBorderWidth: 1,
                        plotBorderColor: '#ccc',
                        height: 250,
                    },
                    credits: {enabled: false},
                    exporting: {enabled: false},
                    title: {text: null },
                    tooltip: {
                        borderWidth: 1,
                        backgroundColor: '#ffffff',
                    },
                    plotOptions: {
                        scatter: {
                            shadow: false,
                            showInLegend: false,
                        },
                        boxplot: {},
                    },
                    xAxis: [
                        {
                            type: "category",
                            categories: ['Distribution stat'],
                            title: {
                                text: '',
                            },
                            visible: false,                            
                            labels: {
                                enabled: false,
                                formatter: function(label) {
                                    return (+label.value).toFixed(3);
                                }
                            },
                        },],
                    yAxis: [
                        {
                            title: {
                                text: category
                            },
                            visible: true,                          
                        },
                    ],
                    series: [
                        {
                            name: '',
                            type: 'boxplot',                            
                            visible: true,
                            color: '#D66BA0',
                            showInLegend: false,
                            data: [boxplotData],
                        },
                        {
                            name: 'Outliers',
                            color: Highcharts.getOptions().colors[0],
                            type: 'scatter',
                            data: outliers,
                            marker: {
                                fillColor: 'white',
                                lineWidth: 1,
                                lineColor: 'grey',                                
                            },
                            tooltip: {
                                pointFormat: 'Observation: {point.y}'
                            }
                        }
                    ]
                } as Highcharts.Options
        }

    distributionButtonClick(action: string) {
        if (action === 'clean') {
            this.dataCleaned = true;
            this.getDistributionValues('1');
        }
        
        if (action === 'reset') {
            this.dataCleaned = false;
            this.distributionInfo.cleanedByMinAndMax = "0";
            this.getDistributionValues('0');
        }
        
        if (action === 'apply') {
            this.updateKpiGrid();
            this.checkForErrors();
        }

        if (action === 'save') {
            if (this.isErrorFree) {
                this.saveToDatabase(); 
            }            
        }
    }
    
    saveToDatabase() {
        let data = this.gridData.data;
        if (data && data[1]) {
            let updatedData = data[1];
            this._kpiConfigService.saveKPIConfig([updatedData]).subscribe(
                (res: number) => {
                    if (res !== null && res > 0) {
                        this.saveMessageColor = 'green';
                        this.saveMessage = 'Changes have been saved';
                        this.incomingData.dataBackToParent.next(updatedData);
                    }
                    else {
                        this.saveMessage = 'Something went wrong. Please check.';
                        this.saveMessageColor = 'orange';
                    }
                    this.removeSaveMessage();
                },
                (error) => {
                    this.saveMessageColor = 'orange';
                    this.saveMessage = 'Something went wrong. Please check.';
                    this.removeSaveMessage();
                    throw new Error(error);
                }                
            );
        }
    }
    
    removeSaveMessage() {
        setTimeout(() => {
            this.saveMessage = '';
            this.saveMessageColor = '';
        }, 5000);
    }
    
    updateKpiGrid() {        
        let data = [this.data];        
        let updatedData = Object.assign({}, this.data);
        
        updatedData['status'] = 'Updating to';
        this.setKPIValues();
        let kpiValues = this.configValueSet;
        updatedData.range1End = kpiValues[0];
        updatedData.range2Start = kpiValues[1];
        updatedData.range2End = kpiValues[2];
        updatedData.range3Start = kpiValues[3]
        updatedData.target = kpiValues[4];
        updatedData.range3End = kpiValues[5];
        updatedData.range4Start = kpiValues[6];
        updatedData.range4End = kpiValues[7];
        updatedData.range5Start = kpiValues[8];  
        
        data.push(updatedData);
        this.gridData = {
            data: orderBy(data, this.sort),
            total: 0,
        };   
    }
    
    setKPIValues() {
        let precision = this.data.precision;
        let target = this.distributionInfo.mean;
        let stdFactor = 0.5;
        let deviationFactor = (this.distributionInfo.std * stdFactor);

        let nextValueDifference = Math.pow(10, precision * -1);
        this.configValueSet = [
            +((target - (2 * deviationFactor)).toFixed(precision)) - nextValueDifference,
            +(target - (2 * deviationFactor)).toFixed(precision),
            +((target - deviationFactor).toFixed(precision)) - nextValueDifference,
            +(target - deviationFactor).toFixed(precision),
            +(target.toFixed(precision)),
            +((target + deviationFactor).toFixed(precision)),
            +((target + deviationFactor).toFixed(precision)) + nextValueDifference,
            +(target + (2 * deviationFactor)).toFixed(precision),
            +((target + (2 * deviationFactor)).toFixed(precision)) + nextValueDifference,
        ]; 
        
        if (this.data.bestScore === 'Lowest') {
            this.configValueSet.sort((a, b) => -1);
        }
    }

    updatingToValueChange(changingFieldId: string, changeToFieldId: string, position: number) {
        let precision = this.data.precision;
        let nextValueDifference = Math.pow(10, precision * -1);
        let operator = 1;
        if (this.data.bestScore === 'Lowest') {
            nextValueDifference = nextValueDifference * -1;
        }   
        
        if (position > 4) {
            operator = 1;
        }
        else if (position < 4) {
            operator = -1;
        }

        let data = this.gridData.data;
        
        if (data[1]) {
            let changingValue = data[1][changingFieldId];
            if (position != 4) {
                data[1][changeToFieldId] = changingValue + nextValueDifference * operator;
            }                  
        }
        this.checkForErrors();
    }
    
    checkForErrors() {
        let fieldSequence = [
            {
                fieldId: 'range1End',
                fieldOrder: 0,
                isAdjustable: false,
            },
            {
                fieldId: 'range2Start',
                fieldOrder: 1,
                isAdjustable: true,
                errorFieldId: 'range2StartError',
            },
            {
                fieldId: 'range2End',
                fieldOrder: 2,
                isAdjustable: false,
            },
            {
                fieldId: 'range3Start',
                fieldOrder: 3,
                isAdjustable: true,
                errorFieldId: 'range3StartError',
            },
            {
                fieldId: 'target',
                fieldOrder: 4,
                isAdjustable: true,
                errorFieldId: 'targetError',
            },
            {
                fieldId: 'range3End',
                fieldOrder: 5,
                isAdjustable: true,
                errorFieldId: 'range3EndError',
            },
            {
                fieldId: 'range4Start',
                fieldOrder: 6,
                isAdjustable: false,
            },
            {
                fieldId: 'range4End',
                fieldOrder: 7,
                isAdjustable: true,
                errorFieldId: 'range4EndError',
            },
            {
                fieldId: 'range5Start',
                fieldOrder: 8,
                isAdjustable: false,
            },
        ];
        
        let dataSet: IKPIConfigData[] = this.gridData.data;
        if (dataSet[1]) {
            let data = dataSet[1];
            let errorCount = 0;

            fieldSequence.forEach((field, index) => {                
                if (
                    index > 0 
                    && index < fieldSequence.length 
                    && field.isAdjustable
                    && field.errorFieldId
                ) {
                    let isDataValid = false;
                    let valueBefore = data[fieldSequence[index - 1].fieldId];
                    let thisValue = data[fieldSequence[index].fieldId];
                    let valueAfter = data[fieldSequence[index + 1].fieldId];
                    
                    if (data.bestScore === 'Lowest') {
                        isDataValid = valueBefore > thisValue && thisValue > valueAfter;
                    }
                    else {
                        isDataValid = valueBefore < thisValue && thisValue < valueAfter;
                    }
                    data[field.errorFieldId] = !isDataValid;
                    if (!isDataValid) {
                        errorCount ++;
                    }
                }
            });
            this.isErrorFree = errorCount === 0;
        }
        this.setButtonActiveState();
    }
    
    setButtonActiveState() {
        let cleanBtn = this.buttonOptions.find((btn) => btn.id === 'clean');
        let applyBtn = this.buttonOptions.find((btn) => btn.id === 'apply');
        let saveBtn = this.buttonOptions.find((btn) => btn.id === 'save');
        let resetBtn = this.buttonOptions.find((btn) => btn.id === 'reset');
        
        if (cleanBtn && this.distributionInfo.outlierCount === 0)  {
            cleanBtn.disabled = true;
        }
        else {
            cleanBtn.disabled = false;
        }

        if (applyBtn && this.distributionInfo.count === 0)  {
            applyBtn.disabled = true;
        }
        else {
            applyBtn.disabled = false;
        }
        
        if (saveBtn && !(this.gridData.data && this.gridData.data[1] && this.isErrorFree)) {
            saveBtn.disabled = true;
        }
        else {
            saveBtn.disabled = false;
        }
        
        if (this.dataCleaned) {
            resetBtn.disabled = false;
        }
        else {
            resetBtn.disabled = true;
        }
    }
}
